import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { IPagination } from 'src/app/shared/models/pagination';
import { environment } from 'src/environments/environment';
import { IipObservation } from '../interfaces/Iip-observation';
import AllSettlementResponse from '../interfaces/ip-all-settlement-response';
import FilterValues from '../interfaces/ip-filter-values';
import { PageResponse } from '../interfaces/ip-page-response';
import SettlementUpdateRequest from '../interfaces/ip-settlement-edit-request';
import SettlementResponse from '../interfaces/ip-settlement-response';
import SettlementSaleAndSettlementResponse from '../interfaces/ip-settlement-sale-and-settlement-response';
import SettlementSaleUpdateRequest from '../interfaces/ip-settlement-sale-update-request';
import SettlementStatus from '../interfaces/ip-settlement-status';
import { QueryParametersService } from './ip-query-parameter.service';

@Injectable({
  providedIn: 'root',
})
export class IpSettlementService {
  private api = environment.api;

  private settlementStatusesSubject = new BehaviorSubject<SettlementStatus[] | null>(null);
  settlementStatuses$ = this.settlementStatusesSubject.asObservable();


  constructor(
    private http: HttpClient,
    private queryParameterService: QueryParametersService
  ) {
    this.getAllSettlementStatuses().subscribe();
  }

  // SETTLEMENTS

  getData(
    pagination: IPagination,
    sortFieldName: string = '',
    sortOrder: string = '',
    searchName: string = '',
    searchValue: string = '',
    statusCode: string = '',
    billed?: boolean,
    filterValues?: FilterValues
  ): Observable<PageResponse<AllSettlementResponse>> {
    const params = this.queryParameterService.buildQueryParameters(
      sortFieldName,
      sortOrder,
      pagination,
      searchName,
      searchValue,
      filterValues,
      0, // A trick
      0, // A trick
      statusCode,
      billed
    );
    return this.http.get<PageResponse<AllSettlementResponse>>(
      `${this.api}/invopay/settlements`,
      { params }
    );
  }

  getDataBySupplierId(
    supplierId: number,
    pagination: IPagination,
    sortFieldName: string = '',
    sortOrder: string = '',
    searchName: string = '',
    searchValue: string = '',
    statusCode: string = '',
    filterValues?: FilterValues
  ): Observable<PageResponse<AllSettlementResponse>> {
    const params = this.queryParameterService.buildQueryParameters(
      sortFieldName,
      sortOrder,
      pagination,
      searchName,
      searchValue,
      filterValues,
      0, // A trick
      0, // A trick
      statusCode
    );
    return this.http.get<PageResponse<AllSettlementResponse>>(
      `${this.api}/invopay/settlements/supplier/${supplierId}`,
      { params }
    );
  }

  getSettlementById(id: number): Observable<SettlementResponse> {
    return this.http.get<SettlementResponse>(
      `${this.api}/invopay/settlements/${id}`
    );
  }

  getApprovedSettlements(
    brokerId?: number,
    currencyCodes?: string[]
  ): Observable<SettlementResponse[]> {
    if (brokerId) {
      let params = new HttpParams()
      if (currencyCodes && currencyCodes.length > 0) {
        params = params.set('currencyCodes', currencyCodes.join(','));
      }
      return this.http.get<SettlementResponse[]>(
        `${this.api}/invopay/settlements/readAllApproved/${brokerId}`,
        { params }
      );
    }
    return this.http.get<SettlementResponse[]>(
      `${this.api}/invopay/settlements/readAllApproved`
    );
  }

  changeSettlementStatus(
    id: number,
    statusCode: string,
    observation?: string
  ): Observable<SettlementResponse> {
    return this.http.post<SettlementResponse>(
      `${this.api}/invopay/settlements/${id}?newStatusCode=${statusCode}&observation=${observation}`,
      {}
    );
  }

  updateSettlement(val: string): Observable<SettlementResponse> {
    return this.http.put<SettlementResponse>(
      `${this.api}/invopay/settlements/edit`,
      val
    );
  }

  editSettlement(
    request: SettlementUpdateRequest
  ): Observable<SettlementResponse> {
    const formattedBody = this.toFormData(request);

    return this.http.put<SettlementResponse>(
      `${this.api}/invopay/settlements/edit`,
      request
    );
  }

  deleteSettlement(id: number): Observable<SettlementResponse> {
    return this.http.delete<SettlementResponse>(
      `${this.api}/invopay/settlements/${id}`
    );
  }

  // SETTLEMENT SALES

  updateSettlementSale(
    request: SettlementSaleUpdateRequest,
    id: number
  ): Observable<SettlementSaleAndSettlementResponse> {
    return this.http.put<SettlementSaleAndSettlementResponse>(
      `${this.api}/invopay/settlements/sales/${id}`,
      request
    );
  }

  deleteSettlementSale(
    id: number
  ): Observable<SettlementSaleAndSettlementResponse> {
    return this.http.delete<SettlementSaleAndSettlementResponse>(
      `${this.api}/invopay/settlements/sales/${id}`
    );
  }

  // SETTLEMENT STATUSES AND STATUS TRANSITIONS

  getAllSettlementStatuses(): Observable<Array<SettlementStatus>> {
    return this.http.get<Array<SettlementStatus>>(
      `${this.api}/invopay/settlements/status`
    ).pipe(
      tap((statuses) => this.settlementStatusesSubject.next(statuses))
    );
  }

  getNextPossibleStatuses(
    statusCode: string,
    settlementId?: number
  ): Observable<Array<SettlementStatus>> {
    return this.http.get<Array<SettlementStatus>>(
      `${this.api}/invopay/settlements/status-transitions/${statusCode}`,
      { params: settlementId ? { settlementId: settlementId } : {} }
    );
  }

  // OTHERS

  toFormData(formValue: any) {
    const formData = new FormData();
    for (const key of Object.keys(formValue)) {
      let value = formValue[key];

      formData.append(key, value);
    }
    return formData;
  }

  // COMMENTS

  getSettlementObservations(id: number): Observable<IipObservation[]> {
    return this.http.get<IipObservation[]>(
      `${this.api}/invopay/observations/settlement/${id}`
    );
  }

  createSettlementObservation(
    id: number,
    description: string
  ): Observable<IipObservation> {
    return this.http.post<IipObservation>(
      `${this.api}/invopay/observations/settlement?settlementId=${id}`,
      { description }
    );
  }

  sendSettlementInformationEmailPdf(
    settlementId: number,
  ): Observable<void> {
    return this.http.post<void>(`${this.api}/invopay/settlements/send-email/${settlementId}`,
      {});
  }

  getSettlementsWithoutInvoice(
    enterpriseId?: number
  ): Observable<SettlementResponse[]> {
    return this.http.get<SettlementResponse[]>(
      `${this.api}/invopay/settlements/without-invoice/${enterpriseId || ''}`
    );
  }

  getSettlementPdf(settlementId: number): Observable<Blob> {
    return this.http.get(`${this.api}/invopay/settlements/send-email/${settlementId}`, {
      responseType: 'blob'
    });
  }

  getApprovedSettlementsBroker(
    currencyCodes?: string[]
  ): Observable<SettlementResponse[]> {
    let params = new HttpParams()
    if (currencyCodes && currencyCodes.length > 0) {
      params = params.set('currencyCodes', currencyCodes.join(','));
    }
    return this.http.get<SettlementResponse[]>(
      `${this.api}/invopay/settlements/readAllApproved/broker`,
      { params }
    );
  }
}
